import {
  CorporateProjectsOverviewResponse,
  createCorporateProjectsOverviewPageQuery,
  getCorporateProjects,
  getCorporateProjectsOverviewPage,
  GetCorporateProjectsResponse,
  imageUrlFor,
  SiteIdTypeWithoutProjects,
  sortCorporateProjectsByIdArray,
} from '@klokgroep/sanity';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import {
  getPropertiesWithPreviewAndSitewideData,
  PropertiesWithPreviewNavigationAndSocialsAndMetaData,
} from '@klokgroep/shared-components/utils/getPropertiesWithPreviewNavigationAndSocials';
import { ChangeEventHandler, Fragment, useCallback, useMemo } from 'react';
import { getCorporateSiteIdPaths } from 'utils/hostnames';
import { getFilterPropertiesForKey, getFilteredProjects } from '@klokgroep/shared-components/utils/projectFilterUtils';
import { getRevalidateInterval } from '@klokgroep/shared-components/utils/getRevalidateInterval';
import { MaxWidth } from '@klokgroep/shared-components/src/MaxWidth';
import { Pagination } from '@klokgroep/shared-components/src/Pagination';
import { PortableTextWithLinks } from '@klokgroep/shared-components/src/PortableTextWithLinks';
import { ProjectsFilters } from '@klokgroep/shared-components/src/ProjectsFilters';
import { ProjectsGrid } from '@klokgroep/shared-components/src/ProjectsGrid';
import { Map } from '@klokgroep/shared-components/src/Map';
import { RichText } from '@klokgroep/shared-components/src/RichText';
import { useRouter } from 'next/router';
import styles from './index.module.css';
import { useCommonTranslations } from '@klokgroep/shared-components/utils/useTranslations';
import { BlockContainerWithBackground } from '@klokgroep/shared-components/src/BlockContainerWithBackground';

const MAP_ZOOM = 8;

const ITEMS_PER_PAGE = 9;

interface Properties extends PropertiesWithPreviewNavigationAndSocialsAndMetaData<CorporateProjectsOverviewResponse> {
  corporateProjects?: GetCorporateProjectsResponse[];
}

const CorporateProjectsOverviewPage: NextPage<Properties> = ({
  intro,
  corporateProjects = [],
  mapStyleString,
  siteId,
}) => {
  const router = useRouter();
  const t = useCommonTranslations();

  const onSearchChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const query = { ...router.query, page: 1, query: event.target.value };
      router.replace({ query }, { pathname: router.asPath.split('?')[0], query }, { shallow: true });
    },
    [router]
  );

  const filterProperties = useMemo(
    () => ({
      locationType: getFilterPropertiesForKey(
        corporateProjects,
        router,
        'locationType',
        t('projects_overview_filters_location_type')
      ),
      status: getFilterPropertiesForKey(corporateProjects, router, 'status', t('projects_overview_filters_status')),
      activities: getFilterPropertiesForKey(
        corporateProjects,
        router,
        'activities',
        t('projects_overview_filters_activities')
      ),
      workAreas: getFilterPropertiesForKey(
        corporateProjects,
        router,
        'workAreas',
        t('projects_overview_filters_work_areas')
      ),
      concepts: getFilterPropertiesForKey(
        corporateProjects,
        router,
        'concepts',
        t('projects_overview_filters_concepts')
      ),
      features: getFilterPropertiesForKey(
        corporateProjects,
        router,
        'features',
        t('projects_overview_filters_features')
      ),
    }),
    [corporateProjects, router, t]
  );

  const filteredProjects = useMemo(
    () =>
      getFilteredProjects(
        corporateProjects,
        // Object entries cannot be typed correctly so here we ignore the [key] does not match error since we know its the correct type
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        Object.fromEntries(Object.entries(filterProperties).map(([key, value]) => [key, value.selected])),
        router.query.query?.toString()
      ),
    [corporateProjects, filterProperties, router.query.query]
  );

  const activePage = useMemo(() => Number.parseInt(router.query?.page?.toString() || '1'), [router.query.page]);

  const paginatedProjects = useMemo(
    () => filteredProjects.slice((activePage - 1) * ITEMS_PER_PAGE, activePage * ITEMS_PER_PAGE),
    [activePage, filteredProjects]
  );

  const totalPages = useMemo(() => Math.ceil(filteredProjects.length / ITEMS_PER_PAGE), [filteredProjects.length]);

  const mapMarkers = useMemo(
    () =>
      filteredProjects.map((item) => ({
        description: item.properties?.status?.map((item) => item?.label).join(' • '),
        imageUrl: item.image?.asset ? imageUrlFor(item.image?.asset).url() : undefined,
        lat: item.geo?.lat,
        lng: item.geo?.lng,
        name: item.title,
        slug: item.slug?.current,
      })),
    [filteredProjects]
  );

  const backgroundColor = useMemo(() => (siteId === 'novaform' ? 'lightGray' : 'white'), [siteId]);
  const MAP_CENTER = useMemo(
    () => (siteId === 'novaform-pl' ? { lat: 52.409_875, lng: 16.895_141 } : { lat: 52.092_876, lng: 5.104_48 }),
    [siteId]
  );
  return (
    <Fragment>
      <Map markers={mapMarkers} zoom={MAP_ZOOM} center={MAP_CENTER} styleString={mapStyleString} />
      <BlockContainerWithBackground backgroundColor={backgroundColor}>
        <MaxWidth>
          <div className={styles.titleContainer}>
            <RichText>
              <h1 className={styles.title}>
                {t('projects_overview_title')} <span>({filteredProjects?.length || 0})</span>
              </h1>

              {!!intro && (
                <div className={styles.textContainer}>
                  <PortableTextWithLinks content={intro} />
                </div>
              )}
            </RichText>
          </div>
          <div className={styles.filtersContainer}>
            <ProjectsFilters
              filterProperties={filterProperties}
              onSearchChange={onSearchChange}
              searchValue={router.query?.query?.toString()}
            />
          </div>
        </MaxWidth>
        <MaxWidth>
          <ProjectsGrid projects={paginatedProjects} />
          {totalPages > 1 ? (
            <div className={styles.paginationContainer}>
              <Pagination activePage={activePage} query={router.query} totalPages={totalPages} />
            </div>
          ) : undefined}
        </MaxWidth>
      </BlockContainerWithBackground>
    </Fragment>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = getCorporateSiteIdPaths();
  return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps<
  Properties,
  { siteId: SiteIdTypeWithoutProjects },
  { token?: string }
> = async ({ preview = false, params, previewData }) => {
  const corporateProjectsOverviewPage = await getCorporateProjectsOverviewPage(preview, params?.siteId);

  if (!corporateProjectsOverviewPage) {
    return { notFound: true, revalidate: getRevalidateInterval() };
  }

  const idsInOrder = corporateProjectsOverviewPage?.order?.map(({ _ref }) => _ref);
  const corporateProjects = await getCorporateProjects(preview, {
    siteIds: [params?.siteId || ''],
  });
  const corporateProjectsWithOrder = sortCorporateProjectsByIdArray(corporateProjects, idsInOrder);
  const corporateProjectsWithOrderWithoutChildProjects = corporateProjectsWithOrder.filter(
    ({ childProjectsAmount }) => childProjectsAmount === 0
  );

  const dataForPreview = {
    query: createCorporateProjectsOverviewPageQuery(params?.siteId || 'vandeklok'),
    token: previewData?.token,
  };

  const propertiesWithSiteWideData = await getPropertiesWithPreviewAndSitewideData(
    preview,
    dataForPreview,
    { ...corporateProjectsOverviewPage, corporateProjects: corporateProjectsWithOrderWithoutChildProjects },
    {
      description: corporateProjectsOverviewPage?.seo?.pageSeo?.description,
      image: corporateProjectsOverviewPage?.seo?.pageSeo?.image,
      title: corporateProjectsOverviewPage?.seo?.pageSeo?.title,
      noIndex: corporateProjectsOverviewPage?.seo?.pageSeo?.noIndex,
      schema: {
        type: 'CollectionPage',
        socials: corporateProjectsOverviewPage?.seo?.socials,
        organisation: {
          name: corporateProjectsOverviewPage?.seo?.sitewideSeo?.organisationName,
          legalName: corporateProjectsOverviewPage?.seo?.sitewideSeo?.organisationLegalName,
          description: corporateProjectsOverviewPage?.seo?.sitewideSeo?.organisationDescription,
          founderName: corporateProjectsOverviewPage?.seo?.sitewideSeo?.organisationFounder,
          foundingDate: corporateProjectsOverviewPage?.seo?.sitewideSeo?.organisationFoundingDate,
          numberOfEmployees: corporateProjectsOverviewPage?.seo?.sitewideSeo?.organisationNumberOfEmployees,
          logo: corporateProjectsOverviewPage?.seo?.sitewideSeo?.organisationLogo,
          slogan: corporateProjectsOverviewPage?.seo?.sitewideSeo?.organisationSlogan,
        },
      },
    },
    params?.siteId
  );

  return {
    props: { ...propertiesWithSiteWideData, siteId: params?.siteId },
    revalidate: getRevalidateInterval(),
  };
};

export default CorporateProjectsOverviewPage;
